import { useMemo } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { FeatureConfig } from '@platform/client/features';
import type {
  FeatureFlagConfig,
  WhatsNewItem,
} from '@platform/client/features/feature-config';

import { CipherGuard } from '@features/cipher-chatbot/index';
import { DashboardLayout } from '@packages/fenrir/dashboard/dashboard-layout';
import { useLocaleStorage } from '@packages/fenrir/hooks/use-locale-storage';
import { ConfirmationProvider } from 'ae-kernel/confirmation-context/confirmation-context';

import { AppBar } from './app-bar/app-bar';
import { LoadingOutlet } from './loading-outlet';
import { PrintHeaderContent } from './print-header-content/print-header-content';
import { useAppSwitchMenuItems } from './use-app-switch-menu-items/use-app-switch-menu-items';
import { useNavigation } from './use-navigation/use-navigation';
import { useOrganizationAccess } from './use-organization-access';

type DashboardElementProps = {
  featureConfigs: FeatureConfig[];
  featureFlagConfigs: FeatureFlagConfig[];
};

export const DashboardElement = ({
  featureConfigs,
  featureFlagConfigs,
}: DashboardElementProps) => {
  const { data: access, isLoading: isLoadingAccessData } =
    useOrganizationAccess();
  const { t } = useTranslation('app');

  const appSwitchMenuItems = useAppSwitchMenuItems();

  const featureFlagKeys = useMemo(
    () =>
      featureFlagConfigs.flatMap(({ featureFlags }) =>
        Object.keys(featureFlags || {}),
      ),
    [featureFlagConfigs],
  );

  const featureFlags = useLocaleStorage(featureFlagKeys);

  const navigation = useNavigation(featureConfigs, featureFlags);

  const whatsNew = useMemo(() => {
    const defaultWhatsNew: WhatsNewItem[] = [
      {
        newsItemId: 'navigationAndAppearance',
        date: dayjs('2025-05-15T00:00:00Z'),
        hasAccess: true,
      },
      {
        newsItemId: 'productUpdates',
        date: null,
        hasAccess: true,
      },
    ];

    const featureReleases = featureConfigs
      .flatMap((feature) =>
        feature?.whatsNew?.({
          ...access,
          featureFlags,
        }),
      )
      .filter((newsItems) => newsItems !== undefined);

    return [...featureReleases, ...defaultWhatsNew];
  }, [access, featureFlags, featureConfigs]);

  const appBar = useMemo(
    () => ({
      children: (
        <AppBar appSwitchItems={appSwitchMenuItems} whatsNew={whatsNew} />
      ),
    }),
    [appSwitchMenuItems, whatsNew],
  );

  const printHeader = useMemo(
    () => ({
      children: (
        <PrintHeaderContent organizationName={access.organization?.name} />
      ),
    }),
    [access.organization?.name],
  );

  return (
    <DashboardLayout
      applicationName={t('applicationName')}
      navigation={navigation}
      appBar={appBar}
      printHeader={printHeader}
      isNavigationLoading={isLoadingAccessData}
    >
      {/* TODO: @evagm: Create new fenrir version of ConfirmationProvider */}
      <ConfirmationProvider
        defaultParams={{
          cancelText: t('confirmation:cancel'),
          confirmText: t('confirmation:ok'),
          description: t('confirmation:areYouSure'),
          title: t('confirmation:confirmation'),
        }}
      >
        <LoadingOutlet isLoadingAccessData={isLoadingAccessData} />
        <CipherGuard />
      </ConfirmationProvider>
    </DashboardLayout>
  );
};
